import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  applyAdminArticleSummary,
  createAdminKeyword,
  getAdminArticleReports,
  getAdminArticleSummaries,
  getAdminBlogs,
  getAdminBlogsRequiringSelector,
  getAdminKeywordOverview,
  getAdminRecentArticles,
  mergeAdminKeywordsById,
  previewAdminArticleResummary,
  suggestAdminCssSelector,
  updateAdminArticleCreatedDate,
  updateAdminArticleVisibility,
  updateAdminBlog,
  updateAdminBlogCssSelectorByUrl,
  updateAdminKeywordById,
  updateAdminKeywordHead,
} from "../api";
import {
  AdminArticleReportResponse,
  AdminArticleReportStatus,
  AdminArticleSummaryResponse,
  AdminBlogResponse,
  AdminBlogRequiringSelectorResponse,
  AdminCssSelectorSuggestResponse,
  AdminFollowerKeywordOverviewResponse,
  AdminHeadKeywordOverviewResponse,
  AdminKeywordOverviewResponse,
  AdminRecentArticleResponse,
  SessionState,
} from "../types";
import { EmptyState } from "../components/EmptyState";
import { Toast } from "../components/Toast";
import { Link, useSearchParams } from "react-router-dom";

type AdminPageProps = {
  session: SessionState;
  loginUrl: string;
};

type AdminTab = "articles" | "blogs" | "keywords" | "crawler";

type AdminToast = {
  id: number;
  message: string;
  tone: "success" | "error" | "info";
  durationMs?: number;
};

type DragKeywordItem = {
  id: number;
  type: "head" | "follower";
  label: string;
  headId: number | null;
};

type KeywordActionTarget = {
  id: number;
  value: string;
  isVisible: boolean;
  type: "head" | "follower";
  headId: number | null;
};

type LoadOptions = {
  reset: boolean;
  silent?: boolean;
};

const REPORT_STATUS_OPTIONS: Array<{ value: AdminArticleReportStatus; label: string }> = [
  { value: "RECEIVED", label: "접수" },
  { value: "REVIEWED", label: "검토" },
  { value: "RESOLVED", label: "처리 완료" },
  { value: "DISMISSED", label: "기각" },
];

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString("ko-KR", {
      hour12: false,
    });
  } catch (_) {
    return value;
  }
}

function reportStatusLabel(status: AdminArticleReportStatus): string {
  const found = REPORT_STATUS_OPTIONS.find((option) => option.value === status);
  return found?.label ?? status;
}

function rssStatusLabel(
  status: AdminBlogRequiringSelectorResponse["rssStatus"] | AdminBlogResponse["rssStatus"]
): string {
  switch (status) {
    case "NO_RSS":
      return "RSS 없음";
    case "WITHOUT_CONTENT":
      return "RSS 본문 없음";
    case "WITH_CONTENT":
      return "RSS 본문 포함";
    default:
      return status;
  }
}

function normalizeError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return fallback;
}

function toEpoch(value: string): number {
  const epoch = new Date(value).getTime();
  return Number.isFinite(epoch) ? epoch : 0;
}

function sortFollowersByVisibleAndRecency(
  followers: AdminFollowerKeywordOverviewResponse[]
): AdminFollowerKeywordOverviewResponse[] {
  return [...followers].sort((a, b) => {
    if (a.isVisible !== b.isVisible) {
      return a.isVisible ? -1 : 1;
    }
    const createdDiff = toEpoch(b.createdAt) - toEpoch(a.createdAt);
    if (createdDiff !== 0) {
      return createdDiff;
    }
    return b.id - a.id;
  });
}

function sortHeadKeywordsByVisibleAndRecency(
  headKeywords: AdminHeadKeywordOverviewResponse[]
): AdminHeadKeywordOverviewResponse[] {
  return [...headKeywords]
    .map((headKeyword) => ({
      ...headKeyword,
      followers: sortFollowersByVisibleAndRecency(headKeyword.followers),
    }))
    .sort((a, b) => {
      if (a.isVisible !== b.isVisible) {
        return a.isVisible ? -1 : 1;
      }
      const createdDiff = toEpoch(b.createdAt) - toEpoch(a.createdAt);
      if (createdDiff !== 0) {
        return createdDiff;
      }
      return b.id - a.id;
    });
}

function normalizeKeywordOverview(
  overview: AdminKeywordOverviewResponse
): AdminKeywordOverviewResponse {
  return {
    ...overview,
    headKeywords: sortHeadKeywordsByVisibleAndRecency(overview.headKeywords),
  };
}

function parseAdminTab(value: string | null): AdminTab | null {
  switch (value) {
    case "articles":
    case "blogs":
    case "keywords":
    case "crawler":
      return value;
    default:
      return null;
  }
}

export function AdminPage({ session, loginUrl }: AdminPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = session.token;
  const isAuthenticated = session.status === "authenticated" && !!token;
  const isAdmin = !!session.user?.isAdmin;

  const [activeTab, setActiveTab] = useState<AdminTab>("articles");

  const [toast, setToast] = useState<AdminToast | null>(null);
  const toastSeqRef = useRef(0);
  const toastTimerRef = useRef<number | null>(null);

  const [recentArticles, setRecentArticles] = useState<AdminRecentArticleResponse[]>([]);
  const [recentNext, setRecentNext] = useState<number | null>(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentLoadingMore, setRecentLoadingMore] = useState(false);
  const [recentInitialized, setRecentInitialized] = useState(false);
  const [editingDates, setEditingDates] = useState<Record<number, string>>({});
  const [savingDateArticleId, setSavingDateArticleId] = useState<number | null>(null);
  const [updatingVisibilityArticleId, setUpdatingVisibilityArticleId] = useState<number | null>(null);
  const [openSummaryArticleId, setOpenSummaryArticleId] = useState<number | null>(null);
  const [summaryLoadingArticleId, setSummaryLoadingArticleId] = useState<number | null>(null);
  const [creatingSummaryArticleId, setCreatingSummaryArticleId] = useState<number | null>(null);
  const [applyingSummaryArticleId, setApplyingSummaryArticleId] = useState<number | null>(null);
  const [summariesByArticleId, setSummariesByArticleId] = useState<
    Record<number, AdminArticleSummaryResponse[]>
  >({});
  const [selectedSummaryByArticleId, setSelectedSummaryByArticleId] = useState<
    Record<number, number | null>
  >({});
  const [articleReportLoadingId, setArticleReportLoadingId] = useState<number | null>(null);
  const [openArticleReportsForId, setOpenArticleReportsForId] = useState<number | null>(null);
  const [articleReportsByArticleId, setArticleReportsByArticleId] = useState<
    Record<number, AdminArticleReportResponse[]>
  >({});
  const [focusedArticleId, setFocusedArticleId] = useState<number | null>(null);
  const [adminBlogs, setAdminBlogs] = useState<AdminBlogResponse[]>([]);
  const [adminBlogNext, setAdminBlogNext] = useState<number | null>(null);
  const [adminBlogLoading, setAdminBlogLoading] = useState(false);
  const [adminBlogLoadingMore, setAdminBlogLoadingMore] = useState(false);
  const [adminBlogInitialized, setAdminBlogInitialized] = useState(false);
  const [adminBlogQuery, setAdminBlogQuery] = useState("");
  const [adminBlogSearchInput, setAdminBlogSearchInput] = useState("");
  const [savingBlogId, setSavingBlogId] = useState<number | null>(null);

  const [keywordOverview, setKeywordOverview] = useState<AdminKeywordOverviewResponse | null>(null);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [keywordUpdating, setKeywordUpdating] = useState(false);
  const [creatingKeyword, setCreatingKeyword] = useState(false);
  const [newKeywordValue, setNewKeywordValue] = useState("");
  const [newKeywordVisible, setNewKeywordVisible] = useState(true);
  const [keywordSearchQuery, setKeywordSearchQuery] = useState("");
  const [draggingKeyword, setDraggingKeyword] = useState<DragKeywordItem | null>(null);
  const [droppingHeadId, setDroppingHeadId] = useState<number | null>(null);
  const [selectedKeywordTarget, setSelectedKeywordTarget] = useState<KeywordActionTarget | null>(null);
  const [mergeKeywordQuery, setMergeKeywordQuery] = useState("");
  const [mergeTargetKeywordId, setMergeTargetKeywordId] = useState<number | null>(null);
  const [includeKeywordQuery, setIncludeKeywordQuery] = useState("");
  const [includeKeywordIds, setIncludeKeywordIds] = useState<number[]>([]);

  const [cssSuggestBlogUrl, setCssSuggestBlogUrl] = useState("");
  const [cssSuggestLimit, setCssSuggestLimit] = useState("10");
  const [cssSuggestSampleSize, setCssSuggestSampleSize] = useState("5");
  const [cssSuggestFirstTitle, setCssSuggestFirstTitle] = useState("");
  const [cssSuggestFirstUrl, setCssSuggestFirstUrl] = useState("");
  const [cssSuggesting, setCssSuggesting] = useState(false);
  const [applyingSuggestedSelector, setApplyingSuggestedSelector] = useState<string | null>(null);
  const [cssSuggestResult, setCssSuggestResult] = useState<AdminCssSelectorSuggestResponse | null>(
    null
  );
  const [blogsRequiringSelector, setBlogsRequiringSelector] = useState<
    AdminBlogRequiringSelectorResponse[]
  >([]);
  const [blogsRequiringSelectorLoading, setBlogsRequiringSelectorLoading] = useState(false);
  const [blogsRequiringSelectorLoaded, setBlogsRequiringSelectorLoaded] = useState(false);
  const articleIdFromQuery = useMemo(() => {
    const rawValue = searchParams.get("articleId");
    if (!rawValue) {
      return null;
    }
    const parsed = Number(rawValue);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }, [searchParams]);
  const tabFromQuery = useMemo(() => parseAdminTab(searchParams.get("tab")), [searchParams]);

  useEffect(() => {
    if (!tabFromQuery || activeTab === tabFromQuery) {
      return;
    }
    setActiveTab(tabFromQuery);
  }, [activeTab, tabFromQuery]);

  function handleTabChange(nextTab: AdminTab) {
    setActiveTab(nextTab);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === "articles") {
      nextSearchParams.delete("tab");
    } else {
      nextSearchParams.set("tab", nextTab);
    }
    setSearchParams(nextSearchParams, { replace: true });
  }

  function showToast(
    message: string,
    tone: AdminToast["tone"] = "info",
    durationMs = 3200
  ) {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastSeqRef.current += 1;
    setToast({
      id: toastSeqRef.current,
      message,
      tone,
      durationMs,
    });
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, durationMs);
  }

  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  async function loadRecentArticles({ reset, silent = false }: LoadOptions) {
    if (!token) return;
    if (reset) {
      setRecentLoading(true);
    } else {
      setRecentLoadingMore(true);
    }
    try {
      const response = await getAdminRecentArticles(token, {
        next: reset ? null : recentNext,
        size: 20,
      });
      setRecentArticles((prev) => {
        if (reset) {
          return response.items;
        }
        const merged = [...prev];
        const exists = new Set(prev.map((item) => item.id));
        response.items.forEach((item) => {
          if (!exists.has(item.id)) {
            merged.push(item);
          }
        });
        return merged;
      });
      setEditingDates((prev) => {
        const nextState: Record<number, string> = reset ? {} : { ...prev };
        response.items.forEach((item) => {
          if (!nextState[item.id]) {
            nextState[item.id] = item.createdDate;
          }
        });
        return nextState;
      });
      setRecentNext(response.next ?? null);
      if (!silent) {
        showToast("최근 게시글 목록을 불러왔습니다.", "success");
      }
    } catch (error) {
      showToast(normalizeError(error, "최근 게시글 조회에 실패했습니다."), "error");
    } finally {
      if (reset) {
        setRecentLoading(false);
        setRecentInitialized(true);
      } else {
        setRecentLoadingMore(false);
      }
    }
  }

  async function loadKeywordOverview(silent = false) {
    if (!token) return;
    setKeywordLoading(true);
    try {
      const result = await getAdminKeywordOverview(token);
      setKeywordOverview(normalizeKeywordOverview(result));
      if (!silent) {
        showToast("키워드 현황을 불러왔습니다.", "success");
      }
    } catch (error) {
      showToast(normalizeError(error, "키워드 현황 조회에 실패했습니다."), "error");
    } finally {
      setKeywordLoading(false);
    }
  }

  async function loadBlogsNeedSelector(silent = false) {
    if (!token) return;
    setBlogsRequiringSelectorLoading(true);
    try {
      const result = await getAdminBlogsRequiringSelector(token);
      setBlogsRequiringSelector(result);
      if (!silent) {
        showToast("selector 적용이 필요한 블로그 목록을 불러왔습니다.", "success");
      }
    } catch (error) {
      showToast(normalizeError(error, "selector 대상 블로그 목록 조회에 실패했습니다."), "error");
    } finally {
      setBlogsRequiringSelectorLoading(false);
      setBlogsRequiringSelectorLoaded(true);
    }
  }

  async function loadAdminBlogs({ reset, silent = false, query }: LoadOptions & { query?: string }) {
    if (!token) return;
    const resolvedQuery = query ?? adminBlogQuery;
    if (reset) {
      setAdminBlogLoading(true);
    } else {
      setAdminBlogLoadingMore(true);
    }
    try {
      const response = await getAdminBlogs(token, {
        next: reset ? null : adminBlogNext,
        size: 20,
        query: resolvedQuery.trim() ? resolvedQuery.trim() : undefined,
      });
      setAdminBlogs((prev) => {
        if (reset) {
          return response.items;
        }
        const merged = [...prev];
        const existingIds = new Set(prev.map((item) => item.id));
        response.items.forEach((item) => {
          if (!existingIds.has(item.id)) {
            merged.push(item);
          }
        });
        return merged;
      });
      setAdminBlogQuery(resolvedQuery);
      if (reset) {
        setAdminBlogSearchInput(resolvedQuery);
      }
      setAdminBlogNext(response.next ?? null);
      if (!silent) {
        showToast("블로그 목록을 불러왔습니다.", "success");
      }
    } catch (error) {
      showToast(normalizeError(error, "블로그 목록 조회에 실패했습니다."), "error");
    } finally {
      if (reset) {
        setAdminBlogLoading(false);
        setAdminBlogInitialized(true);
      } else {
        setAdminBlogLoadingMore(false);
      }
    }
  }

  useEffect(() => {
    if (!isAuthenticated || !isAdmin || !token) {
      return;
    }
    if (activeTab === "articles") {
      if (!recentLoading && !recentInitialized) {
        void loadRecentArticles({ reset: true, silent: true });
      }
      return;
    }
    if (activeTab === "blogs") {
      if (!adminBlogLoading && !adminBlogInitialized) {
        void loadAdminBlogs({ reset: true, silent: true });
      }
      return;
    }
    if (activeTab === "keywords" && !keywordLoading && !keywordOverview) {
      void loadKeywordOverview(true);
      return;
    }
    if (activeTab === "crawler" && !blogsRequiringSelectorLoading && !blogsRequiringSelectorLoaded) {
      void loadBlogsNeedSelector(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    isAuthenticated,
    isAdmin,
    token,
    recentInitialized,
    recentLoading,
    adminBlogInitialized,
    adminBlogLoading,
    keywordLoading,
    keywordOverview,
    blogsRequiringSelectorLoading,
    blogsRequiringSelectorLoaded,
  ]);

  useEffect(() => {
    if (activeTab !== "articles" || !recentInitialized || articleIdFromQuery === null) {
      return;
    }
    const targetExists = recentArticles.some((article) => article.id === articleIdFromQuery);
    if (!targetExists) {
      return;
    }
    setFocusedArticleId(articleIdFromQuery);
    const targetElement = document.getElementById(`admin-article-${articleIdFromQuery}`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const timerId = window.setTimeout(() => {
      setFocusedArticleId((current) => (current === articleIdFromQuery ? null : current));
    }, 2400);
    return () => window.clearTimeout(timerId);
  }, [activeTab, articleIdFromQuery, recentArticles, recentInitialized]);

  async function ensureSummaries(articleId: number) {
    if (!token) return;
    setSummaryLoadingArticleId(articleId);
    try {
      const summaries = await getAdminArticleSummaries(token, articleId);
      setSummariesByArticleId((prev) => ({
        ...prev,
        [articleId]: summaries,
      }));
      const applied = summaries.find((summary) => summary.isApplied)?.id ?? summaries[0]?.id ?? null;
      setSelectedSummaryByArticleId((prev) => ({
        ...prev,
        [articleId]: prev[articleId] ?? applied,
      }));
    } catch (error) {
      showToast(normalizeError(error, "요약 목록 조회에 실패했습니다."), "error");
    } finally {
      setSummaryLoadingArticleId(null);
    }
  }

  async function handleToggleSummaries(articleId: number) {
    if (openSummaryArticleId === articleId) {
      setOpenSummaryArticleId(null);
      return;
    }
    setOpenSummaryArticleId(articleId);
    if (!summariesByArticleId[articleId]) {
      await ensureSummaries(articleId);
    }
  }

  async function handleResummarize(articleId: number) {
    if (!token) return;
    setCreatingSummaryArticleId(articleId);
    try {
      const preview = await previewAdminArticleResummary(token, [articleId]);
      const item = preview.items[0];
      if (!item || !item.success || item.candidateSummaryId === null) {
        showToast(item?.errorMessage ?? "재요약 후보 생성에 실패했습니다.", "error");
        return;
      }
      await ensureSummaries(articleId);
      setOpenSummaryArticleId(articleId);
      setSelectedSummaryByArticleId((prev) => ({
        ...prev,
        [articleId]: item.candidateSummaryId,
      }));
      showToast("새 요약 후보를 만들었습니다. 적용할 요약본을 선택해 주세요.", "success");
    } catch (error) {
      showToast(normalizeError(error, "재요약 실행에 실패했습니다."), "error");
    } finally {
      setCreatingSummaryArticleId(null);
    }
  }

  async function handleApplySummary(articleId: number) {
    if (!token) return;
    const summaryId = selectedSummaryByArticleId[articleId];
    if (!summaryId) {
      showToast("적용할 요약본을 선택해 주세요.", "error");
      return;
    }

    setApplyingSummaryArticleId(articleId);
    try {
      const applied = await applyAdminArticleSummary(token, summaryId);
      await ensureSummaries(articleId);
      setRecentArticles((prev) =>
        prev.map((item) =>
          item.id === articleId
            ? {
                ...item,
                summary: applied.summary,
                summarizedBy: applied.summarizedBy,
              }
            : item
        )
      );
      showToast("선택한 요약본을 적용했습니다.", "success");
    } catch (error) {
      showToast(normalizeError(error, "요약 적용에 실패했습니다."), "error");
    } finally {
      setApplyingSummaryArticleId(null);
    }
  }

  async function handleDateSave(articleId: number) {
    if (!token) return;
    const createdDate = editingDates[articleId];
    if (!createdDate) {
      showToast("날짜를 입력해 주세요.", "error");
      return;
    }
    setSavingDateArticleId(articleId);
    try {
      const result = await updateAdminArticleCreatedDate(token, articleId, createdDate);
      setRecentArticles((prev) =>
        prev.map((item) =>
          item.id === articleId
            ? {
                ...item,
                createdDate: result.createdDate,
              }
            : item
        )
      );
      setEditingDates((prev) => ({
        ...prev,
        [articleId]: result.createdDate,
      }));
      showToast("게시글 날짜를 수정했습니다.", "success");
    } catch (error) {
      showToast(normalizeError(error, "게시글 날짜 수정에 실패했습니다."), "error");
    } finally {
      setSavingDateArticleId(null);
    }
  }

  async function handleToggleArticleVisibility(articleId: number, isVisible: boolean) {
    if (!token) return;
    setUpdatingVisibilityArticleId(articleId);
    try {
      const result = await updateAdminArticleVisibility(token, articleId, !isVisible);
      setRecentArticles((prev) =>
        prev.map((item) =>
          item.id === articleId
            ? {
                ...item,
                isVisible: result.isVisible,
              }
            : item
        )
      );
      showToast(
        result.isVisible ? "게시글을 노출 상태로 변경했습니다." : "게시글을 비노출 상태로 변경했습니다.",
        "success"
      );
    } catch (error) {
      showToast(normalizeError(error, "게시글 노출 상태 변경에 실패했습니다."), "error");
    } finally {
      setUpdatingVisibilityArticleId(null);
    }
  }

  function handleAdminBlogFieldChange(blogId: number, patch: Partial<AdminBlogResponse>) {
    setAdminBlogs((prev) =>
      prev.map((blog) =>
        blog.id === blogId
          ? {
              ...blog,
              ...patch,
            }
          : blog
      )
    );
  }

  async function handleSaveBlog(blogId: number) {
    if (!token) return;
    const target = adminBlogs.find((blog) => blog.id === blogId);
    if (!target) return;

    setSavingBlogId(blogId);
    try {
      const updated = await updateAdminBlog(token, blogId, {
        name: target.name.trim(),
        url: target.url.trim(),
        image: target.image?.trim() || null,
        rss: target.rss?.trim() || null,
        urlCssSelector: target.urlCssSelector?.trim() || null,
        rssStatus: target.rssStatus,
        isShowOnMain: target.isShowOnMain,
      });
      setAdminBlogs((prev) =>
        prev.map((blog) =>
          blog.id === blogId
            ? {
                ...updated,
              }
            : blog
        )
      );
      showToast(`"${updated.name}" 블로그 정보를 저장했습니다.`, "success");
    } catch (error) {
      showToast(normalizeError(error, "블로그 정보 저장에 실패했습니다."), "error");
    } finally {
      setSavingBlogId(null);
    }
  }

  async function handleAdminBlogSearch(event: FormEvent) {
    event.preventDefault();
    await loadAdminBlogs({
      reset: true,
      query: adminBlogSearchInput,
    });
  }

  function handleAdminBlogSearchReset() {
    setAdminBlogSearchInput("");
    void loadAdminBlogs({
      reset: true,
      query: "",
    });
  }

  async function toggleArticleReports(articleId: number) {
    if (!token) return;
    if (openArticleReportsForId === articleId) {
      setOpenArticleReportsForId(null);
      return;
    }

    setOpenArticleReportsForId(articleId);
    if (articleReportsByArticleId[articleId]) {
      return;
    }

    setArticleReportLoadingId(articleId);
    try {
      const reports = await getAdminArticleReports(token, articleId);
      setArticleReportsByArticleId((prev) => ({
        ...prev,
        [articleId]: reports,
      }));
    } catch (error) {
      showToast(normalizeError(error, "게시글 신고 내역 조회에 실패했습니다."), "error");
    } finally {
      setArticleReportLoadingId(null);
    }
  }

  function recalculateKeywordOverviewCounts(
    headKeywords: AdminHeadKeywordOverviewResponse[]
  ): Pick<
    AdminKeywordOverviewResponse,
    "totalKeywordCount" | "headKeywordCount" | "followerKeywordCount" | "visibleKeywordCount" | "hiddenKeywordCount"
  > {
    const headKeywordCount = headKeywords.length;
    const followerKeywordCount = headKeywords.reduce(
      (count, headKeyword) => count + headKeyword.followers.length,
      0
    );
    const totalKeywordCount = headKeywordCount + followerKeywordCount;
    const visibleKeywordCount = headKeywords.reduce(
      (count, headKeyword) =>
        count +
        (headKeyword.isVisible ? 1 : 0) +
        headKeyword.followers.filter((follower) => follower.isVisible).length,
      0
    );
    const hiddenKeywordCount = totalKeywordCount - visibleKeywordCount;
    return {
      totalKeywordCount,
      headKeywordCount,
      followerKeywordCount,
      visibleKeywordCount,
      hiddenKeywordCount,
    };
  }

  function moveFollowerLocally(
    sourceFollowerId: number,
    targetHeadKeywordId: number | null
  ) {
    setKeywordOverview((prev) => {
      if (!prev) {
        return prev;
      }

      let movingFollower: AdminFollowerKeywordOverviewResponse | null = null;
      const removedHeadKeywords = prev.headKeywords.map((headKeyword) => {
        const nextFollowers = headKeyword.followers.filter((follower) => {
          if (follower.id === sourceFollowerId) {
            movingFollower = follower;
            return false;
          }
          return true;
        });
        if (nextFollowers.length === headKeyword.followers.length) {
          return headKeyword;
        }
        return {
          ...headKeyword,
          followers: nextFollowers,
        };
      });

      if (!movingFollower) {
        return prev;
      }
      const follower = movingFollower as AdminFollowerKeywordOverviewResponse;

      let nextHeadKeywords: AdminHeadKeywordOverviewResponse[];
      if (targetHeadKeywordId === null) {
        const newHead: AdminHeadKeywordOverviewResponse = {
          id: follower.id,
          value: follower.value,
          isVisible: follower.isVisible,
          articleCount: 0,
          createdAt: follower.createdAt,
          followers: [],
        };
        nextHeadKeywords = [...removedHeadKeywords, newHead];
      } else {
        nextHeadKeywords = removedHeadKeywords.map((headKeyword) => {
          if (headKeyword.id !== targetHeadKeywordId) {
            return headKeyword;
          }
          return {
            ...headKeyword,
            followers: [...headKeyword.followers, follower],
          };
        });
      }

      const sortedHeadKeywords = sortHeadKeywordsByVisibleAndRecency(nextHeadKeywords);
      const nextCounts = recalculateKeywordOverviewCounts(sortedHeadKeywords);
      return {
        ...prev,
        ...nextCounts,
        headKeywords: sortedHeadKeywords,
      };
    });
  }

  async function handleToggleKeywordVisible(
    keywordId: number,
    value: string,
    isVisible: boolean
  ): Promise<boolean> {
    if (!token) return false;
    const nextVisible = !isVisible;
    setKeywordUpdating(true);
    try {
      const updatedOverview = await updateAdminKeywordById(token, keywordId, { isVisible: nextVisible });
      setKeywordOverview(normalizeKeywordOverview(updatedOverview));
      showToast(`키워드 "${value}"의 노출 상태를 바꿨습니다.`, "success");
      return true;
    } catch (error) {
      showToast(normalizeError(error, "키워드 상태 변경에 실패했습니다."), "error");
      return false;
    } finally {
      setKeywordUpdating(false);
    }
  }

  async function handleCreateKeyword(event: FormEvent) {
    event.preventDefault();
    if (!token) return;

    const value = newKeywordValue.trim();
    if (!value) {
      showToast("추가할 키워드를 입력해 주세요.", "error");
      return;
    }

    setCreatingKeyword(true);
    try {
      const created = await createAdminKeyword(token, {
        value,
        isVisible: newKeywordVisible,
      });
      await loadKeywordOverview(true);
      setNewKeywordValue("");
      setNewKeywordVisible(true);
      showToast(`키워드 "${created.value}"를 추가했습니다.`, "success");
    } catch (error) {
      showToast(normalizeError(error, "키워드 추가에 실패했습니다."), "error");
    } finally {
      setCreatingKeyword(false);
    }
  }

  async function handleDropToHead(
    targetHeadKeywordId: number,
    sourceKeyword: DragKeywordItem | null = draggingKeyword
  ) {
    if (!token || !sourceKeyword) return;
    if (sourceKeyword.type === "head") {
      if (sourceKeyword.id === targetHeadKeywordId) return;
      setKeywordUpdating(true);
      try {
        const updatedOverview = await mergeAdminKeywordsById(token, sourceKeyword.id, targetHeadKeywordId);
        setKeywordOverview(normalizeKeywordOverview(updatedOverview));
        showToast(`헤드 키워드 "${sourceKeyword.label}"를 병합했습니다.`, "success");
      } catch (error) {
        showToast(normalizeError(error, "키워드 병합에 실패했습니다."), "error");
      } finally {
        setKeywordUpdating(false);
      }
      return;
    }

    if (sourceKeyword.headId === targetHeadKeywordId) return;
    const snapshot = keywordOverview;
    moveFollowerLocally(sourceKeyword.id, targetHeadKeywordId);
    setKeywordUpdating(true);
    try {
      await updateAdminKeywordHead(token, sourceKeyword.id, targetHeadKeywordId);
      showToast(`팔로워 키워드를 이동했습니다.`, "success");
    } catch (error) {
      setKeywordOverview(snapshot);
      showToast(normalizeError(error, "팔로워 이동에 실패했습니다."), "error");
    } finally {
      setKeywordUpdating(false);
    }
  }

  async function handleDropToRoot(sourceKeyword: DragKeywordItem | null = draggingKeyword) {
    if (!token || !sourceKeyword) return;
    if (sourceKeyword.type !== "follower") {
      return;
    }
    const snapshot = keywordOverview;
    moveFollowerLocally(sourceKeyword.id, null);
    setKeywordUpdating(true);
    try {
      await updateAdminKeywordHead(token, sourceKeyword.id, null);
      showToast(`"${sourceKeyword.label}"를 헤드 키워드로 분리했습니다.`, "success");
    } catch (error) {
      setKeywordOverview(snapshot);
      showToast(normalizeError(error, "키워드 분리에 실패했습니다."), "error");
    } finally {
      setKeywordUpdating(false);
    }
  }

  function handleSelectKeywordTarget(target: KeywordActionTarget) {
    setSelectedKeywordTarget(target);
    setMergeKeywordQuery("");
    setMergeTargetKeywordId(null);
    setIncludeKeywordQuery("");
    setIncludeKeywordIds([]);
  }

  async function handleToggleSelectedKeywordVisible() {
    if (!selectedKeywordTarget) {
      showToast("먼저 키워드를 선택해 주세요.", "info");
      return;
    }
    const nextVisible = !selectedKeywordTarget.isVisible;
    const changed = await handleToggleKeywordVisible(
      selectedKeywordTarget.id,
      selectedKeywordTarget.value,
      selectedKeywordTarget.isVisible
    );
    if (!changed) {
      return;
    }
    setSelectedKeywordTarget((prev) =>
      prev
        ? {
            ...prev,
            isVisible: nextVisible,
          }
        : prev
    );
  }

  async function handleMergeSelectedKeyword() {
    if (!token) return;
    if (!selectedKeywordTarget) {
      showToast("먼저 병합할 키워드를 선택해 주세요.", "info");
      return;
    }
    if (!mergeTargetKeywordId) {
      showToast("병합 대상을 선택해 주세요.", "info");
      return;
    }

    const destinationLabel =
      allKeywordTargets.find((item) => item.id === mergeTargetKeywordId)?.value ?? "선택한 키워드";

    setKeywordUpdating(true);
    try {
      const updatedOverview = await mergeAdminKeywordsById(
        token,
        selectedKeywordTarget.id,
        mergeTargetKeywordId
      );
      setKeywordOverview(normalizeKeywordOverview(updatedOverview));
      setSelectedKeywordTarget(null);
      setMergeKeywordQuery("");
      setMergeTargetKeywordId(null);
      showToast(`"${selectedKeywordTarget.value}"를 "${destinationLabel}"로 병합했습니다.`, "success");
    } catch (error) {
      showToast(normalizeError(error, "키워드 병합에 실패했습니다."), "error");
    } finally {
      setKeywordUpdating(false);
    }
  }

  function toggleIncludeKeyword(keywordId: number) {
    setIncludeKeywordIds((prev) => {
      if (prev.includes(keywordId)) {
        return prev.filter((id) => id !== keywordId);
      }
      return [...prev, keywordId];
    });
  }

  async function handleIncludeIntoSelectedKeyword() {
    if (!token) return;
    if (!selectedKeywordTarget) {
      showToast("먼저 기준 키워드를 선택해 주세요.", "info");
      return;
    }
    if (includeKeywordIds.length === 0) {
      showToast("포함할 키워드를 하나 이상 선택해 주세요.", "info");
      return;
    }

    setKeywordUpdating(true);
    try {
      let destinationHeadId = selectedKeywordTarget.id;
      if (selectedKeywordTarget.type === "follower") {
        await updateAdminKeywordHead(token, selectedKeywordTarget.id, null);
      }

      const sourceIds = includeKeywordIds.filter((id) => id !== destinationHeadId);
      if (sourceIds.length === 0) {
        showToast("현재 키워드 외에 포함할 대상을 선택해 주세요.", "info");
        return;
      }
      for (const sourceId of sourceIds) {
        await updateAdminKeywordHead(token, sourceId, destinationHeadId);
      }

      await loadKeywordOverview(true);
      setSelectedKeywordTarget((prev) =>
        prev
          ? {
              ...prev,
              type: "head",
              headId: null,
            }
          : prev
      );
      setIncludeKeywordQuery("");
      setIncludeKeywordIds([]);
      showToast(`선택한 키워드 ${sourceIds.length}개를 "${selectedKeywordTarget.value}"에 포함했습니다.`, "success");
    } catch (error) {
      showToast(normalizeError(error, "키워드 포함 처리에 실패했습니다."), "error");
    } finally {
      setKeywordUpdating(false);
    }
  }

  function closeKeywordActionModal() {
    if (keywordUpdating) {
      return;
    }
    setSelectedKeywordTarget(null);
    setMergeKeywordQuery("");
    setMergeTargetKeywordId(null);
    setIncludeKeywordQuery("");
    setIncludeKeywordIds([]);
  }

  async function handleCssSuggest(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    if (!cssSuggestBlogUrl.trim()) {
      showToast("blogUrl을 입력해 주세요.", "error");
      return;
    }
    if (!cssSuggestFirstTitle.trim() || !cssSuggestFirstUrl.trim()) {
      showToast("첫번째 글 제목과 URL을 함께 입력해 주세요.", "error");
      return;
    }
    setCssSuggesting(true);
    try {
      const result = await suggestAdminCssSelector(token, {
        blogUrl: cssSuggestBlogUrl.trim(),
        candidateLimit: Number(cssSuggestLimit) || 10,
        sampleSize: Number(cssSuggestSampleSize) || 5,
        firstArticleTitle: cssSuggestFirstTitle.trim(),
        firstArticleUrl: cssSuggestFirstUrl.trim(),
      });
      setCssSuggestResult(result);
      showToast("selector 추천을 완료했습니다.", "success");
    } catch (error) {
      showToast(normalizeError(error, "selector 추천에 실패했습니다."), "error");
    } finally {
      setCssSuggesting(false);
    }
  }

  function handleUseBlogForSuggest(blog: AdminBlogRequiringSelectorResponse) {
    setCssSuggestBlogUrl(blog.url);
    showToast(`"${blog.name}" URL을 추천 입력값으로 채웠습니다.`, "info");
  }

  async function handleApplySuggestedSelector(selector: string) {
    if (!token) return;
    const blogUrl = cssSuggestResult?.blogUrl?.trim() || cssSuggestBlogUrl.trim();
    if (!blogUrl) {
      showToast("먼저 추천에 사용할 blogUrl을 입력해 주세요.", "error");
      return;
    }

    setApplyingSuggestedSelector(selector);
    try {
      const result = await updateAdminBlogCssSelectorByUrl(token, {
        blogUrl,
        cssSelector: selector,
        sampleSize: Number(cssSuggestSampleSize) || 5,
        force: false,
      });
      if (result.saved) {
        await loadBlogsNeedSelector(true);
      }
      showToast(
        result.saved
          ? "선택한 selector를 바로 적용했습니다."
          : result.message ?? "테스트 실패로 저장하지 않았습니다.",
        result.saved ? "success" : "info"
      );
    } catch (error) {
      showToast(normalizeError(error, "selector 적용에 실패했습니다."), "error");
    } finally {
      setApplyingSuggestedSelector(null);
    }
  }

  const keywordHeadList = useMemo(() => keywordOverview?.headKeywords ?? [], [keywordOverview]);

  const filteredKeywordHeadList = useMemo(() => {
    const query = keywordSearchQuery.trim().toLowerCase();
    if (!query) {
      return keywordHeadList;
    }
    return keywordHeadList
      .map((headKeyword) => {
        const headMatches = headKeyword.value.toLowerCase().includes(query);
        if (headMatches) {
          return headKeyword;
        }
        const matchedFollowers = headKeyword.followers.filter((follower) =>
          follower.value.toLowerCase().includes(query)
        );
        if (matchedFollowers.length === 0) {
          return null;
        }
        return {
          ...headKeyword,
          followers: matchedFollowers,
        };
      })
      .filter((item): item is AdminHeadKeywordOverviewResponse => item !== null);
  }, [keywordHeadList, keywordSearchQuery]);

  const allKeywordTargets = useMemo<KeywordActionTarget[]>(() => {
    return keywordHeadList.flatMap((headKeyword) => [
      {
        id: headKeyword.id,
        value: headKeyword.value,
        isVisible: headKeyword.isVisible,
        type: "head" as const,
        headId: null,
      },
      ...headKeyword.followers.map((follower) => ({
        id: follower.id,
        value: follower.value,
        isVisible: follower.isVisible,
        type: "follower" as const,
        headId: headKeyword.id,
      })),
    ]);
  }, [keywordHeadList]);

  const mergeKeywordCandidates = useMemo(() => {
    if (!selectedKeywordTarget) {
      return [];
    }
    const query = mergeKeywordQuery.trim().toLowerCase();
    return allKeywordTargets
      .filter((candidate) => candidate.id !== selectedKeywordTarget.id)
      .filter((candidate) => {
        if (!query) return true;
        return candidate.value.toLowerCase().includes(query);
      })
      .slice(0, 40);
  }, [allKeywordTargets, mergeKeywordQuery, selectedKeywordTarget]);

  const includeKeywordCandidates = useMemo(() => {
    if (!selectedKeywordTarget) {
      return [];
    }
    const query = includeKeywordQuery.trim().toLowerCase();
    return allKeywordTargets
      .filter((candidate) => candidate.id !== selectedKeywordTarget.id)
      .filter((candidate) => {
        if (!query) return true;
        return candidate.value.toLowerCase().includes(query);
      })
      .slice(0, 80);
  }, [allKeywordTargets, includeKeywordQuery, selectedKeywordTarget]);

  useEffect(() => {
    if (!selectedKeywordTarget) {
      return;
    }
    const latest = allKeywordTargets.find((keyword) => keyword.id === selectedKeywordTarget.id);
    if (!latest) {
      setSelectedKeywordTarget(null);
      setMergeKeywordQuery("");
      setMergeTargetKeywordId(null);
      setIncludeKeywordQuery("");
      setIncludeKeywordIds([]);
      return;
    }
    if (
      latest.isVisible !== selectedKeywordTarget.isVisible ||
      latest.value !== selectedKeywordTarget.value ||
      latest.headId !== selectedKeywordTarget.headId
    ) {
      setSelectedKeywordTarget(latest);
    }
  }, [allKeywordTargets, selectedKeywordTarget]);

  useEffect(() => {
    if (!selectedKeywordTarget) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !keywordUpdating) {
        setSelectedKeywordTarget(null);
        setMergeKeywordQuery("");
        setMergeTargetKeywordId(null);
        setIncludeKeywordQuery("");
        setIncludeKeywordIds([]);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [keywordUpdating, selectedKeywordTarget]);

  if (!isAuthenticated) {
    return (
      <section className="page-section">
        <EmptyState
          title="관리자 화면은 로그인 후 사용할 수 있습니다"
          description="관리자 권한 계정으로 로그인해 주세요."
          action={
            <a className="btn btn--primary" href={loginUrl}>
              Google로 로그인
            </a>
          }
        />
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="page-section">
        <EmptyState
          title="접근 권한이 없습니다"
          description="이 화면은 관리자 계정만 접근할 수 있습니다."
        />
      </section>
    );
  }

  return (
    <section className="page-section">
      <div className="page-head">
        <div>
          <h1>운영 관리 콘솔</h1>
        </div>
      </div>

      <div className="admin-layout">
        <aside className="admin-sidebar panel">
          <h2>관리 메뉴</h2>
          <div className="admin-tab-list">
            <button
              type="button"
              className={`admin-tab-btn ${activeTab === "articles" ? "admin-tab-btn--active" : ""}`}
              onClick={() => handleTabChange("articles")}
            >
              게시글 관리
            </button>
            <button
              type="button"
              className={`admin-tab-btn ${activeTab === "blogs" ? "admin-tab-btn--active" : ""}`}
              onClick={() => handleTabChange("blogs")}
            >
              블로그 관리
            </button>
            <Link to="/admin/reports" className="admin-tab-btn">
              신고 관리
            </Link>
            <button
              type="button"
              className={`admin-tab-btn ${activeTab === "keywords" ? "admin-tab-btn--active" : ""}`}
              onClick={() => handleTabChange("keywords")}
            >
              키워드 관리
            </button>
            <button
              type="button"
              className={`admin-tab-btn ${activeTab === "crawler" ? "admin-tab-btn--active" : ""}`}
              onClick={() => handleTabChange("crawler")}
            >
              크롤링 설정
            </button>
          </div>
        </aside>

        <div className="admin-main">
          {activeTab === "articles" ? (
            <section className="panel">
              <div className="panel-actions panel-actions--between">
                <div>
                  <h2>게시글 관리</h2>
                  <p className="panel__description">
                    최근 게시글의 노출/비노출을 관리하고 날짜 수정, 요약본 재실행/적용을 진행할 수 있어요.
                  </p>
                </div>
                <div className="admin-page-actions">
                  <Link to="/admin/reports" className="btn btn--ghost btn--small">
                    신고 관리로 이동
                  </Link>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void loadRecentArticles({ reset: true })}
                    disabled={recentLoading}
                  >
                    새로고침
                  </button>
                </div>
              </div>

              <div className="admin-section-stack">
                <div className="admin-section-title">
                  <h3>최근 생성 게시글</h3>
                </div>
                {recentLoading ? (
                  <p className="status-text">최근 게시글을 불러오는 중...</p>
                ) : recentArticles.length === 0 ? (
                  <p className="status-text">표시할 게시글이 없습니다.</p>
                ) : (
                  <div className="admin-article-list">
                    {recentArticles.map((article) => {
                      const summaries = summariesByArticleId[article.id] ?? [];
                      const selectedSummaryId = selectedSummaryByArticleId[article.id] ?? null;
                      const articleReports = articleReportsByArticleId[article.id] ?? [];
                      return (
                        <article
                          key={article.id}
                          id={`admin-article-${article.id}`}
                          className={`admin-article-card ${
                            focusedArticleId === article.id ? "admin-article-card--focused" : ""
                          }`}
                        >
                            <header className="admin-article-card__head">
                              <div className="admin-article-card__blog">
                                <span className="badge badge--muted">{article.blogName}</span>
                                <span className={`badge ${article.isVisible ? "badge--visible" : "badge--hidden"}`}>
                                  {article.isVisible ? "노출" : "비노출"}
                                </span>
                                <span className="badge badge--soft">
                                  신고 {article.receivedReportCount}/{article.reportCount}
                                </span>
                              </div>
                              <a href={article.url} target="_blank" rel="noreferrer" className="admin-link">
                                원문
                              </a>
                            </header>
                            <h4>{article.title}</h4>
                            <p className="admin-article-card__summary">
                              {article.summary ?? "아직 적용된 요약이 없습니다."}
                            </p>
                            <p className="admin-article-card__meta">
                              by {article.summarizedBy ?? "-"} · 생성일 {article.createdDate}
                            </p>

                            <div className="admin-article-card__actions">
                              <div className="admin-inline-control">
                                <label>생성일</label>
                                <input
                                  type="date"
                                  value={editingDates[article.id] ?? article.createdDate}
                                  onChange={(event) =>
                                    setEditingDates((prev) => ({
                                      ...prev,
                                      [article.id]: event.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--small"
                                  onClick={() => void handleDateSave(article.id)}
                                  disabled={savingDateArticleId === article.id}
                                >
                                  {savingDateArticleId === article.id ? "저장 중..." : "날짜 저장"}
                                </button>
                              </div>

                              <div className="admin-inline-control">
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--small"
                                  onClick={() => void handleToggleSummaries(article.id)}
                                >
                                  {openSummaryArticleId === article.id ? "요약본 접기" : "요약본 선택"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--primary btn--small"
                                  onClick={() => void handleResummarize(article.id)}
                                  disabled={creatingSummaryArticleId === article.id}
                                >
                                  {creatingSummaryArticleId === article.id ? "재실행 중..." : "요약 재실행"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--small"
                                  onClick={() => void toggleArticleReports(article.id)}
                                >
                                  {openArticleReportsForId === article.id ? "신고 접기" : "신고 보기"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--small"
                                  onClick={() => void handleToggleArticleVisibility(article.id, article.isVisible)}
                                  disabled={updatingVisibilityArticleId === article.id}
                                >
                                  {updatingVisibilityArticleId === article.id
                                    ? "처리 중..."
                                    : article.isVisible
                                      ? "비노출 처리"
                                      : "노출 처리"}
                                </button>
                              </div>
                            </div>

                            {openSummaryArticleId === article.id ? (
                              <div className="admin-subpanel">
                                {summaryLoadingArticleId === article.id ? (
                                  <p className="status-text">요약 목록을 불러오는 중...</p>
                                ) : summaries.length === 0 ? (
                                  <p className="status-text">요약 이력이 없습니다.</p>
                                ) : (
                                  <>
                                    <div className="admin-summary-list">
                                      {summaries.map((summary) => (
                                        <label key={summary.id} className="admin-summary-item">
                                          <input
                                            type="radio"
                                            name={`summary-${article.id}`}
                                            checked={selectedSummaryId === summary.id}
                                            onChange={() =>
                                              setSelectedSummaryByArticleId((prev) => ({
                                                ...prev,
                                                [article.id]: summary.id,
                                              }))
                                            }
                                          />
                                          <div>
                                            <p className="admin-summary-item__meta">
                                              summaryId {summary.id} · {summary.summarizedBy} ·{" "}
                                              {summary.isApplied ? "현재 적용" : "미적용"}
                                            </p>
                                            <p>{summary.summary}</p>
                                          </div>
                                        </label>
                                      ))}
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn--primary btn--small"
                                      onClick={() => void handleApplySummary(article.id)}
                                      disabled={applyingSummaryArticleId === article.id}
                                    >
                                      {applyingSummaryArticleId === article.id
                                        ? "적용 중..."
                                        : "선택한 요약본 적용"}
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : null}

                            {openArticleReportsForId === article.id ? (
                              <div className="admin-subpanel">
                                {articleReportLoadingId === article.id ? (
                                  <p className="status-text">신고 내역을 불러오는 중...</p>
                                ) : articleReports.length === 0 ? (
                                  <p className="status-text">신고 내역이 없습니다.</p>
                                ) : (
                                  <div className="admin-inline-report-list">
                                    {articleReports.map((report) => (
                                      <div key={report.id} className="admin-inline-report-item">
                                        <p>
                                          <strong>#{report.id}</strong> · {reportStatusLabel(report.status)} ·{" "}
                                          {formatDateTime(report.createdAt)}
                                        </p>
                                        <p>{report.reason}</p>
                                        {report.detail ? <p>{report.detail}</p> : null}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : null}
                        </article>
                      );
                    })}
                  </div>
                )}
                {articleIdFromQuery !== null &&
                !recentLoading &&
                recentArticles.every((article) => article.id !== articleIdFromQuery) ? (
                  <p className="status-text">
                    요청한 게시글 #{articleIdFromQuery}을 현재 목록에서 찾지 못했습니다. 아래의 더 보기로
                    목록을 확장해 확인해 주세요.
                  </p>
                ) : null}
                {recentNext !== null ? (
                  <div className="more-wrap">
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={() => void loadRecentArticles({ reset: false })}
                      disabled={recentLoadingMore}
                    >
                      {recentLoadingMore ? "불러오는 중..." : "게시글 더 보기"}
                    </button>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {activeTab === "blogs" ? (
            <section className="panel">
              <div className="panel-actions panel-actions--between">
                <div>
                  <h2>블로그 관리</h2>
                  <p className="panel__description">
                    블로그 이름, URL, 이미지, RSS 상태, selector, 메인 노출 여부를 직접 관리할 수 있어요.
                  </p>
                </div>
                <div className="admin-page-actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void loadAdminBlogs({ reset: true })}
                    disabled={adminBlogLoading}
                  >
                    {adminBlogLoading ? "불러오는 중..." : "새로고침"}
                  </button>
                </div>
              </div>

              <form className="admin-form admin-blog-search" onSubmit={handleAdminBlogSearch}>
                <label className="admin-field">
                  <span>블로그 검색</span>
                  <small>이름 또는 URL로 필터링합니다.</small>
                  <input
                    value={adminBlogSearchInput}
                    onChange={(event) => setAdminBlogSearchInput(event.target.value)}
                    placeholder="예: naver, techblog"
                  />
                </label>
                <div className="admin-page-actions">
                  <button type="submit" className="btn btn--primary btn--small" disabled={adminBlogLoading}>
                    검색
                  </button>
                  {adminBlogQuery.trim() ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--small"
                      onClick={handleAdminBlogSearchReset}
                      disabled={adminBlogLoading}
                    >
                      검색 초기화
                    </button>
                  ) : null}
                </div>
              </form>

              {adminBlogLoading ? (
                <p className="status-text">블로그 목록을 불러오는 중...</p>
              ) : adminBlogs.length === 0 ? (
                <p className="status-text">표시할 블로그가 없습니다.</p>
              ) : (
                <div className="admin-blog-list">
                  {adminBlogs.map((blog) => (
                    <article key={blog.id} className="admin-list-item admin-list-item--stack admin-blog-item">
                      <div className="admin-blog-item__head">
                        <div>
                          <strong>{blog.name}</strong>
                          <p>
                            id {blog.id} · 등록 {formatDateTime(blog.createdAt)} · 생성자 {blog.createdBy}
                          </p>
                        </div>
                        <span className={`badge ${blog.isShowOnMain ? "badge--visible" : "badge--hidden"}`}>
                          {blog.isShowOnMain ? "메인 노출" : "메인 비노출"}
                        </span>
                      </div>

                      <div className="admin-form-grid admin-form-grid--blog">
                        <label className="admin-field">
                          <span>이름</span>
                          <input
                            value={blog.name}
                            onChange={(event) =>
                              handleAdminBlogFieldChange(blog.id, {
                                name: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label className="admin-field">
                          <span>URL</span>
                          <input
                            value={blog.url}
                            onChange={(event) =>
                              handleAdminBlogFieldChange(blog.id, {
                                url: event.target.value,
                              })
                            }
                          />
                        </label>
                        <label className="admin-field">
                          <span>이미지 URL</span>
                          <input
                            value={blog.image ?? ""}
                            onChange={(event) =>
                              handleAdminBlogFieldChange(blog.id, {
                                image: event.target.value,
                              })
                            }
                            placeholder="없으면 비워두세요"
                          />
                        </label>
                        <label className="admin-field">
                          <span>RSS URL</span>
                          <input
                            value={blog.rss ?? ""}
                            onChange={(event) =>
                              handleAdminBlogFieldChange(blog.id, {
                                rss: event.target.value,
                              })
                            }
                            placeholder="없으면 비워두세요"
                          />
                        </label>
                        <label className="admin-field">
                          <span>RSS 상태</span>
                          <select
                            value={blog.rssStatus}
                            onChange={(event) =>
                              handleAdminBlogFieldChange(blog.id, {
                                rssStatus: event.target.value as AdminBlogResponse["rssStatus"],
                              })
                            }
                          >
                            <option value="NO_RSS">RSS 없음</option>
                            <option value="WITH_CONTENT">RSS 본문 포함</option>
                            <option value="WITHOUT_CONTENT">RSS 본문 없음</option>
                          </select>
                        </label>
                        <label className="admin-field">
                          <span>URL CSS Selector</span>
                          <input
                            value={blog.urlCssSelector ?? ""}
                            onChange={(event) =>
                              handleAdminBlogFieldChange(blog.id, {
                                urlCssSelector: event.target.value,
                              })
                            }
                            placeholder="NO_RSS 블로그일 때 사용"
                          />
                        </label>
                      </div>

                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={blog.isShowOnMain}
                          onChange={(event) =>
                            handleAdminBlogFieldChange(blog.id, {
                              isShowOnMain: event.target.checked,
                            })
                          }
                        />
                        추천/메인 피드에 블로그 노출
                      </label>

                      <div className="admin-page-actions">
                        <button
                          type="button"
                          className="btn btn--primary btn--small"
                          onClick={() => void handleSaveBlog(blog.id)}
                          disabled={savingBlogId === blog.id}
                        >
                          {savingBlogId === blog.id ? "저장 중..." : "저장"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {adminBlogNext !== null ? (
                <div className="more-wrap">
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void loadAdminBlogs({ reset: false })}
                    disabled={adminBlogLoadingMore}
                  >
                    {adminBlogLoadingMore ? "불러오는 중..." : "블로그 더 보기"}
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {activeTab === "keywords" ? (
            <section className="panel">
              <div className="panel-actions panel-actions--between">
                <div>
                  <h2>키워드 관리</h2>
                  <p className="panel__description">
                    키워드를 눌러 노출/비노출과 병합을 처리하고, 데스크톱에서는 Drag & Drop으로 빠르게 이동할 수 있어요.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn--ghost btn--small"
                  onClick={() => void loadKeywordOverview()}
                  disabled={keywordLoading}
                >
                  {keywordLoading ? "불러오는 중..." : "현황 새로고침"}
                </button>
              </div>

              {keywordOverview ? (
                <div className="admin-metric-grid">
                  <div className="admin-metric">
                    <strong>전체</strong>
                    <span>{keywordOverview.totalKeywordCount}</span>
                  </div>
                  <div className="admin-metric">
                    <strong>헤드</strong>
                    <span>{keywordOverview.headKeywordCount}</span>
                  </div>
                  <div className="admin-metric">
                    <strong>팔로워</strong>
                    <span>{keywordOverview.followerKeywordCount}</span>
                  </div>
                  <div className="admin-metric">
                    <strong>노출</strong>
                    <span>{keywordOverview.visibleKeywordCount}</span>
                  </div>
                  <div className="admin-metric">
                    <strong>비노출</strong>
                    <span>{keywordOverview.hiddenKeywordCount}</span>
                  </div>
                </div>
              ) : null}

              <form className="admin-form keyword-create-form" onSubmit={handleCreateKeyword}>
                <h3 className="subheading">키워드 직접 추가</h3>
                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>키워드</span>
                    <small>헤드 키워드로 추가되며, 필요 시 Drag & Drop으로 병합/분리할 수 있어요.</small>
                    <input
                      value={newKeywordValue}
                      onChange={(event) => setNewKeywordValue(event.target.value)}
                      placeholder="예: 생성형 AI"
                    />
                  </label>
                </div>
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={newKeywordVisible}
                    onChange={(event) => setNewKeywordVisible(event.target.checked)}
                  />
                  추가 즉시 노출 상태로 사용
                </label>
                <button type="submit" className="btn btn--primary btn--small" disabled={creatingKeyword}>
                  {creatingKeyword ? "추가 중..." : "키워드 추가"}
                </button>
              </form>

              <p className="status-text">
                키워드를 누르면 설정 창이 열립니다. 설정 창에서 노출/비노출 전환, 병합, 포함 작업을 진행할
                수 있습니다.
              </p>

              <div className="admin-form keyword-search-box">
                <label className="admin-field">
                  <span>키워드 검색</span>
                  <small>헤드/팔로워 키워드를 이름으로 빠르게 찾습니다.</small>
                  <input
                    value={keywordSearchQuery}
                    onChange={(event) => setKeywordSearchQuery(event.target.value)}
                    placeholder="키워드 검색"
                  />
                </label>
                {keywordSearchQuery.trim() ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => setKeywordSearchQuery("")}
                  >
                    검색 초기화
                  </button>
                ) : null}
              </div>

              <div
                className="keyword-lane-grid-droparea"
                onDragOver={(event) => {
                  if (draggingKeyword?.type === "follower") {
                    event.preventDefault();
                  }
                }}
                onDrop={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest(".keyword-lane")) {
                    return;
                  }
                  event.preventDefault();
                  void handleDropToRoot();
                  setDraggingKeyword(null);
                  setDroppingHeadId(null);
                }}
              >
                <div className="keyword-lane-grid">
                  {filteredKeywordHeadList.map((headKeyword: AdminHeadKeywordOverviewResponse) => (
                    <div
                      key={headKeyword.id}
                      data-head-keyword-id={headKeyword.id}
                      className={`keyword-lane ${droppingHeadId === headKeyword.id ? "is-drop-target" : ""}`}
                      onDragOver={(event) => {
                        if (draggingKeyword) {
                          event.preventDefault();
                          setDroppingHeadId(headKeyword.id);
                        }
                      }}
                      onDragLeave={() => {
                        setDroppingHeadId((prev) => (prev === headKeyword.id ? null : prev));
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        void handleDropToHead(headKeyword.id);
                        setDraggingKeyword(null);
                        setDroppingHeadId(null);
                      }}
                    >
                      <button
                        type="button"
                        className={`keyword-chip keyword-chip--head ${headKeyword.isVisible ? "keyword-chip--visible" : "keyword-chip--hidden"} ${selectedKeywordTarget?.id === headKeyword.id ? "is-selected" : ""}`}
                        title={headKeyword.value}
                        draggable
                        onDragStart={() =>
                          setDraggingKeyword({
                            id: headKeyword.id,
                            type: "head",
                            label: headKeyword.value,
                            headId: null,
                          })
                        }
                        onDragEnd={() => {
                          setDraggingKeyword(null);
                          setDroppingHeadId(null);
                        }}
                        onClick={() =>
                          handleSelectKeywordTarget({
                            id: headKeyword.id,
                            value: headKeyword.value,
                            isVisible: headKeyword.isVisible,
                            type: "head",
                            headId: null,
                          })
                        }
                        disabled={keywordUpdating}
                      >
                        <span className="keyword-chip__title">{headKeyword.value}</span>
                      </button>

                      {headKeyword.followers.length === 0 ? null : (
                        <div className="keyword-lane__followers">
                          {headKeyword.followers.map((follower) => (
                            <button
                              key={follower.id}
                              type="button"
                              className={`keyword-chip ${follower.isVisible ? "keyword-chip--visible" : "keyword-chip--hidden"} ${selectedKeywordTarget?.id === follower.id ? "is-selected" : ""}`}
                              title={follower.value}
                              draggable
                              onDragStart={() =>
                                setDraggingKeyword({
                                  id: follower.id,
                                  type: "follower",
                                  label: follower.value,
                                  headId: headKeyword.id,
                                })
                              }
                              onDragEnd={() => {
                                setDraggingKeyword(null);
                                setDroppingHeadId(null);
                              }}
                              onClick={() =>
                                handleSelectKeywordTarget({
                                  id: follower.id,
                                  value: follower.value,
                                  isVisible: follower.isVisible,
                                  type: "follower",
                                  headId: headKeyword.id,
                                })
                              }
                              disabled={keywordUpdating}
                            >
                              <span className="keyword-chip__title">{follower.value}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {filteredKeywordHeadList.length === 0 ? (
                  <p className="status-text">검색 결과가 없습니다.</p>
                ) : null}
              </div>
            </section>
          ) : null}

          {activeTab === "crawler" ? (
            <section className="panel">
              <div className="panel-actions panel-actions--between">
                <div>
                  <h2>크롤링 selector 관리</h2>
                  <p className="panel__description">
                    selector 추천 한 번으로 후보 확인과 즉시 적용까지 처리합니다.
                  </p>
                </div>
              </div>

              <div className="admin-form">
                <div className="panel-actions panel-actions--between">
                  <h3 className="subheading">selector 적용 필요 블로그</h3>
                  <button
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => void loadBlogsNeedSelector()}
                    disabled={blogsRequiringSelectorLoading}
                  >
                    {blogsRequiringSelectorLoading ? "불러오는 중..." : "새로고침"}
                  </button>
                </div>
                <div className="admin-result-box">
                  <p>
                    RSS가 없고 `urlCssSelector`가 비어 있는 블로그 목록입니다. “이 URL로 추천”을 누르면 아래
                    추천 입력값에 자동으로 채워집니다.
                  </p>
                </div>
                {blogsRequiringSelector.length === 0 ? (
                  <div className="admin-result-box">
                    <p>
                      {blogsRequiringSelectorLoading
                        ? "목록을 불러오는 중입니다."
                        : "현재 selector 적용이 필요한 블로그가 없습니다."}
                    </p>
                  </div>
                ) : (
                  <div className="admin-scroll-list">
                    {blogsRequiringSelector.map((blog) => (
                      <div key={blog.id} className="admin-list-item">
                        <div>
                          <strong>{blog.name}</strong>
                          <p>{blog.url}</p>
                          <p>
                            {rssStatusLabel(blog.rssStatus)} · 등록 {formatDateTime(blog.createdAt)}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => handleUseBlogForSuggest(blog)}
                        >
                          이 URL로 추천
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <form className="admin-form" onSubmit={handleCssSuggest}>
                <h3 className="subheading">selector 추천</h3>
                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>blogUrl</span>
                    <small>게시글 목록이 있는 블로그 페이지 URL</small>
                    <input
                      value={cssSuggestBlogUrl}
                      onChange={(event) => setCssSuggestBlogUrl(event.target.value)}
                      placeholder="https://example.com/blog"
                    />
                  </label>
                  <label className="admin-field">
                    <span>candidateLimit</span>
                    <small>추천 후보 개수(1~20, 클수록 느려짐)</small>
                    <input
                      value={cssSuggestLimit}
                      onChange={(event) => setCssSuggestLimit(event.target.value)}
                      placeholder="10"
                    />
                  </label>
                  <label className="admin-field">
                    <span>sampleSize</span>
                    <small>후보 검증에 사용할 샘플 개수</small>
                    <input
                      value={cssSuggestSampleSize}
                      onChange={(event) => setCssSuggestSampleSize(event.target.value)}
                      placeholder="5"
                    />
                  </label>
                  <label className="admin-field admin-field--full">
                    <span>첫번째 글 제목</span>
                    <small>예시: CI/CD에 기존 Provisioning Profile 유지하기(Git으로 유지하기)</small>
                    <input
                      value={cssSuggestFirstTitle}
                      onChange={(event) => setCssSuggestFirstTitle(event.target.value)}
                      placeholder="첫번째 게시글의 제목을 입력하세요"
                    />
                  </label>
                  <label className="admin-field admin-field--full">
                    <span>첫번째 글 URL</span>
                    <small>예시: https://danawalab.github.io/ios/2025/08/04/iOS-cicd-provisioning-profile.html</small>
                    <input
                      value={cssSuggestFirstUrl}
                      onChange={(event) => setCssSuggestFirstUrl(event.target.value)}
                      placeholder="첫번째 게시글의 URL을 입력하세요"
                    />
                  </label>
                </div>
                <button type="submit" className="btn btn--primary btn--small" disabled={cssSuggesting}>
                  {cssSuggesting ? "추천 중..." : "추천 실행"}
                </button>
                {cssSuggestResult ? (
                  <>
                    {cssSuggestResult.message ? (
                      <div className="admin-result-box">
                        <p>{cssSuggestResult.message}</p>
                      </div>
                    ) : null}
                    <div className="admin-scroll-list">
                      {cssSuggestResult.candidates.map((candidate) => (
                        <div key={candidate.selector} className="admin-list-item admin-list-item--stack">
                          <div>
                            <strong>{candidate.selector}</strong>
                            <p>
                              confidence {candidate.confidence.toFixed(2)} · hint {candidate.hintScore.toFixed(2)} · match{" "}
                              {candidate.matchedElementCount} · url {candidate.extractableUrlCount}
                            </p>
                            <p>
                              첫 글 매칭: 제목 {candidate.firstArticleTitleMatched ? "O" : "X"} · URL{" "}
                              {candidate.firstArticleUrlMatched ? "O" : "X"}
                            </p>
                            {candidate.sampleMatches.length > 0 ? (
                              <ul className="admin-suggest-samples">
                                {candidate.sampleMatches.map((match) => (
                                  <li key={`${candidate.selector}-${match.url}`}>
                                    <a href={match.url} target="_blank" rel="noreferrer">
                                      {match.title}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="btn btn--primary btn--small"
                            onClick={() => void handleApplySuggestedSelector(candidate.selector)}
                            disabled={applyingSuggestedSelector === candidate.selector}
                          >
                            {applyingSuggestedSelector === candidate.selector
                              ? "적용 중..."
                              : "이 selector 적용"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="admin-result-box">
                    <p>
                      추천 실행 시 상위 selector 후보와 샘플 게시글 제목을 확인하고 바로 적용할 수 있습니다.
                    </p>
                  </div>
                )}
              </form>
            </section>
          ) : null}
        </div>
      </div>

      {toast ? (
        <Toast
          key={toast.id}
          message={toast.message}
          tone={toast.tone}
          durationMs={toast.durationMs}
          onClose={() => setToast(null)}
        />
      ) : null}

      {selectedKeywordTarget ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="keyword-action-modal-title"
          onClick={() => closeKeywordActionModal()}
        >
          <div className="modal keyword-action-modal" onClick={(event) => event.stopPropagation()}>
            <h2 id="keyword-action-modal-title">키워드 설정</h2>

            <div className="keyword-action-selected">
              <span className="keyword-action-selected__label">선택됨</span>
              <button
                type="button"
                className={`keyword-chip ${selectedKeywordTarget.isVisible ? "keyword-chip--visible" : "keyword-chip--hidden"} is-selected`}
                title={selectedKeywordTarget.value}
              >
                <span className="keyword-chip__title">{selectedKeywordTarget.value}</span>
              </button>
              <span className="keyword-action-selected__meta">
                {selectedKeywordTarget.type === "head" ? "헤드" : "팔로워"}
              </span>
            </div>

            <div className="keyword-action-buttons">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => void handleToggleSelectedKeywordVisible()}
                disabled={keywordUpdating}
              >
                {selectedKeywordTarget.isVisible ? "비노출로 전환" : "노출로 전환"}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => closeKeywordActionModal()}
                disabled={keywordUpdating}
              >
                닫기
              </button>
            </div>

            <div className="keyword-merge-box">
              <label className="admin-field">
                <span>병합 대상 검색</span>
                <small>병합하면 현재 키워드는 사라지고, 선택한 대상 키워드로 합쳐집니다.</small>
                <input
                  value={mergeKeywordQuery}
                  onChange={(event) => {
                    setMergeKeywordQuery(event.target.value);
                    setMergeTargetKeywordId(null);
                  }}
                  placeholder="병합할 대상 키워드를 검색하세요"
                />
              </label>
              <div className="keyword-merge-candidates">
                {mergeKeywordCandidates.length === 0 ? (
                  <p className="status-text">검색 결과가 없습니다.</p>
                ) : (
                  mergeKeywordCandidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      type="button"
                      className={`keyword-merge-candidate ${mergeTargetKeywordId === candidate.id ? "is-selected" : ""}`}
                      onClick={() => setMergeTargetKeywordId(candidate.id)}
                    >
                      <span>{candidate.value}</span>
                      <small>{candidate.type === "head" ? "헤드" : "팔로워"}</small>
                    </button>
                  ))
                )}
              </div>
              <button
                type="button"
                className="btn btn--primary btn--small"
                onClick={() => void handleMergeSelectedKeyword()}
                disabled={keywordUpdating || !mergeTargetKeywordId}
              >
                {keywordUpdating ? "병합 중..." : "선택한 대상과 병합"}
              </button>
            </div>

            <div className="keyword-merge-box">
              <label className="admin-field">
                <span>이 키워드에 포함시키기</span>
                <small>
                  검색해서 여러 키워드를 선택한 뒤 포함할 수 있습니다.
                  {selectedKeywordTarget.type === "follower"
                    ? " (현재 선택 키워드는 자동으로 헤드로 분리 후 포함됩니다.)"
                    : ""}
                </small>
                <input
                  value={includeKeywordQuery}
                  onChange={(event) => setIncludeKeywordQuery(event.target.value)}
                  placeholder="포함할 키워드를 검색하세요"
                />
              </label>
              <div className="keyword-include-candidates">
                {includeKeywordCandidates.length === 0 ? (
                  <p className="status-text">검색 결과가 없습니다.</p>
                ) : (
                  includeKeywordCandidates.map((candidate) => (
                    <button
                      key={candidate.id}
                      type="button"
                      className={`keyword-include-candidate ${includeKeywordIds.includes(candidate.id) ? "is-selected" : ""}`}
                      onClick={() => toggleIncludeKeyword(candidate.id)}
                    >
                      <span>{candidate.value}</span>
                      <small>{candidate.type === "head" ? "헤드" : "팔로워"}</small>
                    </button>
                  ))
                )}
              </div>
              <div className="keyword-action-buttons">
                <span className="status-text">선택 {includeKeywordIds.length}개</span>
                <button
                  type="button"
                  className="btn btn--primary btn--small"
                  onClick={() => void handleIncludeIntoSelectedKeyword()}
                  disabled={keywordUpdating || includeKeywordIds.length === 0}
                >
                  {keywordUpdating ? "처리 중..." : "선택 키워드 포함"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
